import React, { useState, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { FaTimes, FaUpload, FaPlus } from 'react-icons/fa';
import CustomButton from '../CustomButton';
import { verifyProfile } from '../../Services/User/Profile';
import { showToast } from '../Toast/Toast';
import './VerifyProfileModal.css';

const VerifyProfileModal = ({ show, onHide }) => {
  const [idCard, setIdCard] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const idCardInputRef = useRef(null);
  const selfieInputRef = useRef(null);

  const handleIdCardChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setIdCard(file);
    }
  };

  const handleSelfieChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelfie(file);
    }
  };

  const handleRemoveIdCard = () => {
    setIdCard(null);
    if (idCardInputRef.current) {
      idCardInputRef.current.value = '';
    }
  };

  const handleRemoveSelfie = () => {
    setSelfie(null);
    if (selfieInputRef.current) {
      selfieInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!idCard) {
      showToast('Please upload your ID card', 'error');
      return;
    }

    if (!selfie) {
      showToast('Please upload your selfie', 'error');
      return;
    }

    try {
      setSubmitting(true);
      const formData = new FormData();
      formData.append('id_card', idCard);
      formData.append('selfie', selfie);

      const response = await verifyProfile(formData);
      
      if (response.status) {
        showToast('Verification request submitted successfully!', 'success');
        handleClose();
      } else {
        showToast(response.message || 'Failed to submit verification', 'error');
      }
    } catch (error) {
      console.error('Error submitting verification:', error);
      showToast(error.message || 'Failed to submit verification', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setIdCard(null);
    setSelfie(null);
    if (idCardInputRef.current) {
      idCardInputRef.current.value = '';
    }
    if (selfieInputRef.current) {
      selfieInputRef.current.value = '';
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered className="verify-profile-modal">
      <Modal.Body className="verify-profile-modal-body">
        <button className="modal-close-btn" onClick={handleClose}>
          <FaTimes />
        </button>

        <div className="verify-profile-content pt-4">
          {/* Title */}
          <h4 className="verify-profile-title">Verify</h4>

          {/* Instructions */}
          <p className="verify-profile-instructions">
            Upload your driver's license or government issued ID and take a selfie to verify your identity. Once your identity is verified you will get a blue check next to your profile name.
          </p>

          {/* Upload ID Card */}
          <div className="upload-section">
            <label 
              htmlFor="id-card-input" 
              className={`upload-button ${idCard ? 'has-file' : ''}`}
            >
              {idCard ? (
                <div className="file-info">
                  <FaUpload className="upload-icon" />
                  <span>{idCard.name}</span>
                  <button 
                    type="button" 
                    className="remove-file-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveIdCard();
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <>
                  <FaUpload className="upload-icon" />
                  <span>Upload Your File</span>
                </>
              )}
            </label>
            <input
              ref={idCardInputRef}
              id="id-card-input"
              type="file"
              accept="image/*,.pdf"
              onChange={handleIdCardChange}
              style={{ display: 'none' }}
            />
          </div>

          {/* Separator */}
          <div className="separator">
            <span>And</span>
          </div>

          {/* Upload Selfie */}
          <div className="upload-section">
            <label 
              htmlFor="selfie-input" 
              className={`upload-button ${selfie ? 'has-file' : ''}`}
            >
              {selfie ? (
                <div className="file-info">
                  <FaPlus className="upload-icon" />
                  <span>{selfie.name}</span>
                  <button 
                    type="button" 
                    className="remove-file-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveSelfie();
                    }}
                  >
                    ×
                  </button>
                </div>
              ) : (
                <>
                  <FaPlus className="upload-icon" />
                  <span>Add Selfie</span>
                </>
              )}
            </label>
            <input
              ref={selfieInputRef}
              id="selfie-input"
              type="file"
              accept="image/*"
              onChange={handleSelfieChange}
              style={{ display: 'none' }}
            />
          </div>

          {/* Submit Button */}
          <div className="verify-profile-actions">
            <CustomButton
              onClick={handleSubmit}
              disabled={submitting || !idCard || !selfie}
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </CustomButton>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default VerifyProfileModal;

